import React from "react";
import { Box, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { LegacyTokens, B13, B13Ink, B13TypeScale } from "../../../theme/b13Tokens";
// Batch 13 Part C — shared table primitives now render off the B13
// palette. Every consumer (list pages family + SessionData) inherits.
// LegacyTokens still imported for the two `radius.*` values that live
// on the legacy token object and did not need to move (radius is not
// a hue, no restyle needed).

// Batch 41 — shared ABA table + filter primitives.
//
// Every ABA list page (Workflows, Prompt Hierarchies, Session Data,
// Programs, Targets) had subtly different filter Select markup, subtly
// different action-column widths, and subtly different right-side
// spacing. That drift is why the same "Status label overlaps All
// statuses" bug reappeared in three places at once. This module is the
// single source for those primitives; a page that wants to look like
// the rest of the module must consume from here.

// ─── Column widths ─────────────────────────────────────────────────────
export const ABA_TABLE_STATUS_WIDTH = 128;
export const ABA_TABLE_UPDATED_WIDTH = 148;
// Batch 46 — tightened from 148 to 132 so the three 36px icon buttons
// (View / Edit / More) don't float against the far-right edge with 40+px
// of empty space to their right. Header + cell must BOTH use align=center
// and this exact width so the label sits over the icons.
export const ABA_TABLE_ACTION_WIDTH = 132;
export const ABA_TABLE_DEFAULT_WIDTH = 120;
export const ABA_TABLE_CELL_PADDING = { xs: 1.5, md: 2 } as const;

// ─── AbaFilterSelect ───────────────────────────────────────────────────
// Wraps MUI TextField(select) with the correct SelectProps to prevent
// the floating label ever overlapping the selected value: the label is
// bound to the field via TextField (correct shrink behaviour) AND a
// renderValue that gives a real string for empty state ("All …").
export type FilterOption = { value: string; label: string };
export const AbaFilterSelect: React.FC<{
  label: string;
  value: string;
  options: FilterOption[];
  emptyLabel?: string;
  onChange: (v: string) => void;
  fullWidth?: boolean;
  minWidth?: number;
}> = ({ label, value, options, emptyLabel = "All", onChange, fullWidth, minWidth = 180 }) => (
  <TextField
    select
    size="small"
    label={label}
    value={value}
    fullWidth={fullWidth}
    onChange={(e) => onChange(e.target.value)}
    // Batch 43 §6 — force label shrink + notched outline. Without these
    // MUI treats value="" as "empty" and lets the label sit inside the
    // field, overlapping the "All …" text supplied by renderValue.
    // With displayEmpty true the field always has visible content, so
    // the label should always float. Also opt into `notched` so the
    // outline cutout matches the shrunk label.
    InputLabelProps={{ shrink: true }}
    sx={{
      minWidth: fullWidth ? undefined : minWidth,
      "& .MuiInputLabel-root": { fontSize: 13, color: B13Ink.ink3 },
      "& .MuiOutlinedInput-root": {
        backgroundColor: B13.surface,
        height: 44,
        borderRadius: `${LegacyTokens.radius.control}px`,
        "& fieldset": { borderColor: B13.line },
        "&:hover fieldset": { borderColor: B13.primary },
        "&.Mui-focused fieldset": { borderColor: B13.primary },
      },
    }}
    SelectProps={{
      displayEmpty: true,
      notched: true,
      renderValue: (v) => {
        const raw = v == null ? "" : String(v);
        if (!raw) return emptyLabel;
        const match = options.find((o) => o.value === raw);
        return match ? match.label : raw.charAt(0).toUpperCase() + raw.slice(1);
      },
    }}
  >
    <MenuItem value="">{emptyLabel}</MenuItem>
    {options.map((o) => (
      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
    ))}
  </TextField>
);

// ─── AbaActionCell ─────────────────────────────────────────────────────
// One dedicated Stack with equal 36px round buttons at the right side
// of a row. Never let action icons render loose inside the last real
// cell — always inside this container so the pill spacing is stable.
// Batch 46 — centred (not flex-end). The dedicated Actions column has a
// fixed 132px width; centring keeps the three buttons visually balanced
// under the "Actions" header instead of stuck to the right edge with
// visible white space to their right.
export const AbaActionCell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Stack
    direction="row"
    spacing={0.5}
    justifyContent="center"
    alignItems="center"
    sx={{ width: "100%", whiteSpace: "nowrap" }}
  >
    {children}
  </Stack>
);

export const AbaRowIconButton: React.FC<{
  title: string;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  children: React.ReactNode;
  color?: string;
}> = ({ title, onClick, children, color }) => (
  <Tooltip title={title} arrow>
    <IconButton
      onClick={onClick}
      aria-label={title}
      sx={{
        width: 36, height: 36, borderRadius: "50%",
        color: color ?? B13Ink.ink3,
        "&:hover": { backgroundColor: B13.canvas, color: color ?? B13Ink.ink2 },
      }}
    >{children}</IconButton>
  </Tooltip>
);

// ─── AbaStatusCell ─────────────────────────────────────────────────────
// Fixed-width, no-wrap wrapper so the status pill never bumps into the
// Updated or Actions column.
export const AbaStatusCell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{
    display: "flex", alignItems: "center",
    whiteSpace: "nowrap",
  }}>
    {children}
  </Box>
);

// ─── AbaUpdatedCell ────────────────────────────────────────────────────
// Calendar icon + human-readable date, no-wrap.
export const AbaUpdatedCell: React.FC<{
  icon?: React.ReactNode;
  value: string;
}> = ({ icon, value }) => (
  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ whiteSpace: "nowrap" }}>
    {icon}
    <Typography sx={{ ...B13TypeScale.body, fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
  </Stack>
);

// ─── formatDate ────────────────────────────────────────────────────────
export function formatAbaDate(raw: string | null | undefined): string {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
